/* eslint-disable @typescript-eslint/no-unused-vars */
import { useFieldArray, Controller } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { SampleSchema } from '@/zod_schemas';
import PatientSampleForm from '../../Project/Forms/PatientSampleForm';
import BtnMenuSample from './BtnMenuSample';
import { getSampleColumns } from './sample/getSampleColumns';
import { defaultSample } from '../../Project/Forms/defaults';
import {
  useAssignPatientSamples,
  useCreateSample,
  useListSamples,
  usePatchPatientSamples,
} from '@/api/sample/sample';

import { usePatientContext } from './usePatientContext';
import AddSampleDialog from './AddSampleDialog';
import { getErrorMessage } from '@/utils/getErrorMessage';
import DeleteConfirmation from '@/components/Dialog/DeleteConfirmation';
import AppDataGrid from '@/components/DataGrid';

const PatientSampleLists = () => {
  const {
    patientId,
    form: {
      control,
      register,
      trigger,
      formState: { errors },
      getValues,
    },
  } = usePatientContext();

  const [open, setOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SampleSchema[]>([]);
  const [isAddSampleVisible, setIsAddSampleVisible] = useState(false);
  const removeSamplePatient = usePatchPatientSamples(patientId);
  const createSample = useCreateSample(patientId);
  const assignSample = useAssignPatientSamples(patientId);
  const resetFields = () => {
    setSelectedRows([]);
  };

  const { append, remove } = useFieldArray({
    control,
    name: 'patient_samples',
    keyName: '_key',
  });

  const {
    fields: sampleFields,
    append: appendSample,
    remove: removeSample,
  } = useFieldArray({
    control,
    name: 'samples',
    keyName: '_key',
  });

  const handleConfirmClick = async () => {
    // Validation check
    if (selectedRows.length === 0) {
      enqueueSnackbar('Please select at least one HPO term', {
        variant: 'warning',
      });
      return;
    }

    const validData = selectedRows.filter(
      (item): item is SampleSchema => item !== undefined,
    );

    const sampleIds = validData
      .map((s) => s.id)
      .filter((id): id is string => typeof id === 'string');

    try {
      if (patientId) {
        // Add the patient_id field to each object (assign hpo to patient)

        await assignSample.mutateAsync({
          id: patientId!,
          sample_ids: sampleIds,
        });

        enqueueSnackbar(
          `Successfully added ${validData.length} HPO term${
            validData.length > 1 ? 's' : ''
          }`,
          {
            variant: 'success',
          },
        );
      } else {
        append(validData);
        enqueueSnackbar(
          `Successfully added ${validData.length} HPO term${
            validData.length > 1 ? 's' : ''
          }`,
          {
            variant: 'success',
          },
        );
      }

      // Reset and close dialog on success
      resetFields();
      setOpen(false);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const handleClearSelection = async () => {
    setSelectedRows([]);
    enqueueSnackbar('Selection cleared', { variant: 'info' });
  };

  const onRemoveSamplePatients = useCallback(
    async (sampleIds: string[]) => {
      if (!patientId) {
        enqueueSnackbar('Missing patient id', { variant: 'error' });
        return;
      }

      try {
        await removeSamplePatient.mutateAsync({
          id: patientId,
          sample_ids: sampleIds,
        });

        enqueueSnackbar('Remove sample successfully', { variant: 'success' });
      } catch (_err) {
        enqueueSnackbar('Failed to remove sample', { variant: 'error' });
      }
    },
    [patientId, removeSamplePatient],
  );

  // =======================
  // Delete confirmation state
  // =======================
  const [deletingRowId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const openDelete = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const columns = getSampleColumns({
    deleteAction: true,
    onClickDelete: (rowId, name) => {
      openDelete(rowId, name);
    },
    deletingRowId,
    isDeleting: removeSamplePatient.isPending,
  });

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    await onRemoveSamplePatients([deleteTarget.id]);

    closeDelete();
  }, [deleteTarget, closeDelete, onRemoveSamplePatients]);

  const handleAddSampleClick = useCallback(
    (sampleCount: number) => {
      const samples = Array.from({ length: sampleCount }, () => ({
        ...defaultSample,
        sample_type: defaultSample.sample_type ?? 'bam',
        gender: defaultSample.gender ?? 'unknown',
      }));

      appendSample(samples); // ONE append only
      setIsAddSampleVisible(true);
    },
    [appendSample],
  );

  const handleCancelClick = () => {
    setIsAddSampleVisible(false);
    removeSample(); // clears samples
  };

  const onCheckValidation = useCallback(async () => {
    const isValid = await trigger('samples');

    if (isValid) {
      try {
        let newSample = getValues('samples') || [];

        if (patientId) {
          // Add the patient_id field to each object(assign sample to patient)
          newSample = newSample.map((sample) => ({
            ...sample,
            patient_id: patientId,
          }));
        }
        createSample
          .mutateAsync(newSample)
          .then(() => {
            enqueueSnackbar('Samples created successfully', {
              variant: 'success',
            });
          })
          .catch((_ex) => {
            enqueueSnackbar('Failed to create sample[1]', {
              variant: 'error',
            });
          });
      } catch (ex) {
        enqueueSnackbar('Failed to create samples', { variant: 'error' });
      }

      setIsAddSampleVisible(false); // Hide the form after adding samples
      remove(); // Reset the form or perform any clean-up
    } else {
      // console.error(errors.samples?.message); // Log validation errors
    }
  }, [getValues, trigger, remove, createSample, patientId]);

  const { data: sampleListData } = useListSamples();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1.5 }}>
        <BtnMenuSample
          handleAddSampleClick={handleAddSampleClick}
          setOpen={setOpen}
        />
      </Box>

      <Controller
        name="patient_samples"
        control={control}
        render={({ field }) => {
          const rows = field.value || [];

          return (
            <AppDataGrid
              key={rows.length} //  Force re-render when rows change
              rows={rows}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              sx={{
                height: 400,
                '& .MuiDataGrid-cell': {
                  alignItems: 'flex-start',
                  py: 1,
                },
                '& .MuiDataGrid-row': {
                  opacity: removeSamplePatient.isPending ? 0.6 : 1,
                },
              }}
              pageSizeOptions={[5]}
              checkboxSelection
              disableRowSelectionOnClick
              getRowId={(row) => (row.id ? row.id : row.sample_id)}
              loading={removeSamplePatient.isPending}
            />
          );
        }}
      />

      <AddSampleDialog
        open={open}
        setOpen={setOpen}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        handleConfirmClick={handleConfirmClick}
        isSubmitting={createSample.isPending}
        handleClearSelection={handleClearSelection}
        sampleListData={sampleListData}
      />

      {isAddSampleVisible && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1.5 }}>
            <Stack spacing={1} direction="row">
              <Button
                type="button"
                variant="contained"
                color="error"
                size="small"
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                size="small"
                onClick={onCheckValidation}
              >
                Confirm
              </Button>
            </Stack>
          </Box>
          {sampleFields.map(({ id }, index) => (
            <PatientSampleForm
              register={register}
              remove={() => removeSample(index)}
              index={index}
              experimentIndex={0}
              key={id}
              errors={errors}
              control={control}
            />
          ))}
        </>
      )}
      <DeleteConfirmation
        open={Boolean(deleteTarget)}
        itemKey={deleteTarget?.name}
        confirmText="delete"
        onClose={closeDelete}
        onDelete={confirmDelete}
        hideArchive
        isDeleting={Boolean(deletingRowId) || removeSamplePatient.isPending}
      />
    </Box>
  );
};

export default PatientSampleLists;
